import React, {useEffect, useRef} from "react";
import {Button, Drawer, Menu, MenuProps} from "antd";
import Sider from "antd/es/layout/Sider";
import {
  BarChartOutlined,
  CalendarOutlined,
  CloseOutlined,
  ClusterOutlined,
  ContactsOutlined,
  EyeOutlined,
  FieldTimeOutlined,
  FileAddOutlined,
  FileImageOutlined,
  FolderViewOutlined,
  HddOutlined,
  HomeOutlined,
  InfoCircleOutlined,
  LoginOutlined,
  LogoutOutlined,
  MedicineBoxOutlined,
  TeamOutlined,
  UsergroupAddOutlined,
  UserOutlined,
} from "@ant-design/icons";
import "./style.less";
import {Link, useLocation} from "react-router-dom";
import * as RouterWays from "../../enums/routes";
import {useAuthController} from "../../controllers/auth-controller";
import {useAuth} from "../../context/authContext";
import {FaUsers} from "react-icons/fa";
import LoginModal from "../modals/login-modal/LoginModal";

type SidebarProps = {
  isMobile: boolean
  isDrawerVisible: boolean
  toggleDrawer: () => void
}

const Sidebar = (props: SidebarProps) => {
  const {isMobile, isDrawerVisible, toggleDrawer} = props
  const authController = useAuthController();
  const {isModalOpen} = authController;
  const {isLoggedIn, currentUser} = useAuth();
  const location = useLocation();
  const previousPathRef = useRef(location.pathname)
  const H24_LINK =
    "https://h24.ua/organizacia/1832-kp-blyznyukivska-crl/doctors";

  useEffect(() => {
    const hasRouteChanged = previousPathRef.current !== location.pathname
    if (hasRouteChanged && isDrawerVisible) {
      toggleDrawer()
    }
    previousPathRef.current = location.pathname
  }, [location.pathname, isDrawerVisible, toggleDrawer]);

  const AuthActions = <div className="sidebar-auth">
    {isLoggedIn ? (
      <Button
        className="sidebar-auth__button"
        icon={<LogoutOutlined/>}
        onClick={authController.confirmLogout}
      >
        <span className="sidebar-auth__text">
          <span className="sidebar-auth__label">Вийти</span>
          {currentUser?.name && <span className="sidebar-auth__user">{currentUser.name}</span>}
        </span>
      </Button>
    ) : (
      <Button
        className="sidebar-auth__button"
        icon={<LoginOutlined/>}
        onClick={authController.toggleModalVisible}
      >Вхід</Button>
    )}
  </div>


  type MenuItem = Required<MenuProps>["items"][number];

  function getItem(
    label: React.ReactNode,
    key: React.Key,
    icon?: React.ReactNode,
    children?: MenuItem[]
  ): MenuItem {
    return {
      key,
      icon,
      children,
      label,
      className: "sidebar_item",
    } as MenuItem;
  }

  const protectedItem = getItem(
    'Лікареві',
    'doctor-menu',
    <MedicineBoxOutlined/>,
    [
      getItem(
        <Link to={RouterWays.SURGERY_REQUESTS}>Заявки на операції</Link>,
        RouterWays.SURGERY_REQUESTS,
        <FileAddOutlined/>,
      ),
      getItem(
        <Link to={RouterWays.SURGERY_SCHEDULE}>Розклад операцій</Link>,
        RouterWays.SURGERY_SCHEDULE,
        <CalendarOutlined/>,
      ),
    ],
  )

  const items = [
    getItem(<Link to={RouterWays.HOME}>Головна</Link>, RouterWays.HOME, <HomeOutlined/>),
    getItem(<Link to={RouterWays.ADMINISTRATION}>Адміністрація</Link>, RouterWays.ADMINISTRATION, <FaUsers/>),
    getItem(<Link to={RouterWays.NEWS}>Новини</Link>, RouterWays.NEWS, <FileImageOutlined/>),
    getItem(<Link to={RouterWays.STRUCTURE}>Структура підприємства</Link>, RouterWays.STRUCTURE, <ClusterOutlined/>),
    getItem(<Link to={RouterWays.DEPARTMENTS}>Відділення</Link>, RouterWays.DEPARTMENTS, <HddOutlined/>),
    getItem(<Link to={RouterWays.ADMISSION_SCHEDULE}>Графік прийому</Link>, RouterWays.ADMISSION_SCHEDULE,
      <FieldTimeOutlined/>),
    getItem(
      <a href={H24_LINK} target="_blank" rel="noopener noreferrer">
        Запис на прийом
      </a>,
      "external-helsi",
      <CalendarOutlined/>
    ),
    getItem(<Link to={RouterWays.PUBLIC_INFORMATION}>Публічна інформація</Link>, RouterWays.PUBLIC_INFORMATION,
      <FolderViewOutlined/>),
    getItem(<Link to={RouterWays.GENERAL_INFORMATION}>Інформація для громадян</Link>, RouterWays.GENERAL_INFORMATION,
      <TeamOutlined/>),
    getItem(<Link to={RouterWays.TENDER_PROCUREMENTS}>Інформація по тендерних
      закупівлях</Link>, RouterWays.TENDER_PROCUREMENTS, <BarChartOutlined/>),
    getItem(<Link to={RouterWays.OVERSIGHT_COMMITTEE}>Спостережна рада</Link>, RouterWays.OVERSIGHT_COMMITTEE,
      <EyeOutlined/>),
    getItem(<Link to={RouterWays.VACANCIES}>Вакансії</Link>, RouterWays.VACANCIES, <UsergroupAddOutlined/>),
    getItem(<Link to={RouterWays.CONTACTS}>Контакти</Link>, RouterWays.CONTACTS, <ContactsOutlined/>),
    getItem(<Link to={RouterWays.ABOUT}>Про заклад</Link>, RouterWays.ABOUT, <InfoCircleOutlined/>),
    isLoggedIn ? protectedItem : null,
  ].filter((item): item is MenuItem => Boolean(item))

  return (
    <div className="sidebar-shell">
      {isMobile ? (
        <Drawer
          className="sidebar_drawer"
          title={<div className="sidebar-drawer-title"><UserOutlined/> Навігація</div>}
          placement="left"
          closable={false}
          open={isDrawerVisible}
          width={300}
          zIndex={1300}
        >
          <Button
            className="sidebar-drawer-close"
            type="text"
            onClick={toggleDrawer}
            icon={<CloseOutlined/>}
            aria-label="Закрити меню"
          />
          <Menu
            className="drawer_menu"
            theme="light"
            selectedKeys={[location.pathname]}
            mode="inline"
            items={items}
          >
          </Menu>
          {AuthActions}
        </Drawer>
      ) : (
        <Sider
          theme="light"
          width={250}
          className="sider"
        >
          <div className="sidebar-navigation-label">Навігація</div>
          <div className="sidebar-menu-scroll">
            <Menu
              theme="light"
              selectedKeys={[location.pathname]}
              mode="inline"
              items={items}
            />
          </div>
          {AuthActions}
        </Sider>
      )}
      {isModalOpen && <LoginModal authController={authController}/>}
    </div>
  );
};

export default Sidebar;
